import { useEffect, useState } from "react";
import { Warning, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * CompAlertBanner — top-of-dashboard sticky non-dismissible banner shown to
 * users whose monthly subscription is required (either the super admin has
 * flipped their comp's dues-required toggle on, OR their comp has expired /
 * been revoked). Tap → Stripe Checkout for the platform monthly subscription.
 *
 * Feb 16 2026 (Randy pt-comp-grant-system) — see COMP_GRANT_SYSTEM_SPEC.md.
 */
export default function CompAlertBanner() {
  const [alert, setAlert] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/me/comp-alert");
        if (!alive) return;
        setAlert(r.data.alert);
      } catch (err) {
        // 401 (logged out) or network noise — silent no-op
        console.debug("[CompAlertBanner] no alert", err?.response?.status);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openCheckout = async () => {
    setOpening(true);
    try {
      const r = await api.post("/stripe/platform/checkout", {
        origin_url: window.location.origin,
        return_path: window.location.pathname,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error("Checkout URL missing — please try again.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open checkout");
    } finally {
      setOpening(false);
    }
  };

  if (!alert) return null;

  const isDuesRequired = alert.kind === "dues_required";
  return (
    <div
      data-testid="comp-alert-banner"
      className={`sticky top-0 z-40 w-full ${isDuesRequired ? "bg-[#FBE9E1]" : "bg-[#F4EEDC]"} border-b hairline shadow-sm`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-col sm:flex-row items-start sm:items-center gap-2">
        <div className="flex items-start gap-2 flex-1">
          <Warning size={18} weight="fill"
                   className={isDuesRequired ? "text-[#C16E5A]" : "text-[#8A6D2D]"} />
          <div className="text-sm text-[#1C1B1A]">
            {alert.message}
          </div>
        </div>
        <button
          type="button"
          onClick={openCheckout}
          disabled={opening}
          data-testid="comp-alert-cta"
          className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm ${isDuesRequired ? "bg-[#C16E5A] hover:bg-[#A85A47]" : "bg-[#4A5D4E] hover:bg-[#3A4A3E]"} text-white disabled:opacity-40`}
        >
          {opening ? "Opening…" : alert.cta_label}
          <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
